import { useState, useEffect, useRef } from "react";

function sameDeps(a, b) {
  return Boolean(a) && a.length === b.length && a.every((v, i) => Object.is(v, b[i]));
}

function useFetch(fn, deps, { skip = false } = {}) {
  const [state, setState] = useState({ data: null, loading: !skip, error: null });

  // The effect below can only raise `loading` after the render that changed the
  // deps has already painted, so callers get one frame reporting "not loading"
  // while `data` is still the previous request's — long enough to flash the old
  // list, or an empty state, before the spinner appears. Comparing the deps
  // during render closes the gap: a request the effect is about to issue counts
  // as loading right away.
  const issuedDeps = useRef(null);
  const currentDeps = [skip, ...deps];
  const pending = !skip && !sameDeps(issuedDeps.current, currentDeps);

  useEffect(() => {
    issuedDeps.current = currentDeps;

    if (skip) {
      setState((s) => ({ ...s, loading: false }));
      return;
    }

    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));

    fn()
      .then((data) => {
        if (!cancelled) setState({ data, loading: false, error: null });
      })
      .catch((err) => {
        if (!cancelled) setState({ data: null, loading: false, error: err.message || "Something went wrong" });
      });

    return () => {
      cancelled = true;
    };
  }, [skip, ...deps]); // eslint-disable-line react-hooks/exhaustive-deps

  return { ...state, loading: state.loading || pending };
}

export default useFetch;
